import supabase from '../utils/supabaseClient';

// Data-access layer for the `subfamilia` table.
// Same contract as familias.js: resolves to plain data, throws the Supabase
// error on failure.

/**
 * Visible subfamilias with a derived `price` (lowest `precio1` of their
 * productos), optionally filtered to a single familia. Capped at 100 rows.
 * @param {{ familiaId?: string|number }} [opts]
 * @returns {Promise<Array>}
 */
export async function getVisibleSubfamiliasWithPrice({ familiaId } = {}) {
  let query = supabase
    .from('subfamilia')
    .select(`
      id,
      nombre,
      familia_id,
      thumbnail,
      hidden,
      producto:producto(precio1)
    `, { count: 'exact' })
    .eq('hidden', false)
    .limit(100);

  if (familiaId) {
    query = query.eq('familia_id', familiaId);
  }

  const { data, error } = await query;
  if (error) throw error;

  return (data || []).map(subf => ({
    ...subf,
    price:
      subf.producto && subf.producto.length > 0
        ? Math.min(...subf.producto.map(p => p.precio1))
        : null,
  }));
}

/**
 * `familia_id` + `thumbnail` for every visible subfamilia.
 * @returns {Promise<Array>}
 */
export async function getVisibleSubfamiliaThumbs() {
  const { data, error } = await supabase
    .from('subfamilia')
    .select('familia_id, thumbnail')
    .eq('hidden', false);

  if (error) throw error;
  return data || [];
}

/**
 * `familia_id` only, for every visible subfamilia — used to work out which
 * familias have at least one visible subfamilia.
 * @returns {Promise<Array>}
 */
export async function getVisibleSubfamiliaFamiliaIds() {
  const { data, error } = await supabase
    .from('subfamilia')
    .select('familia_id')
    .eq('hidden', false);

  if (error) throw error;
  return data || [];
}

/**
 * Every subfamilia row.
 * @returns {Promise<Array>}
 */
export async function getAllSubfamilias() {
  const { data, error } = await supabase.from('subfamilia').select('*');

  if (error) throw error;
  return data || [];
}
